import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { useAvailableAircraftModels } from 'clientState/providers/ParsedSearchResponseProvider';
import {
  useOrderFilterTagsActions,
  useOrderFilterTags,
} from 'clientState/providers/OrderedFilterTagsProvider';
import { AircraftPredicateProvider } from './AircraftPredicate';
import { usePerformedSearchRequest } from 'clientState/providers/SearchRequestProvider';

type Aircraft = { id: string; checked: boolean };
type AircraftState = Aircraft[];
type AircraftAction =
  | { type: 'TOGGLE'; payload: { id: string } }
  | { type: 'SET'; payload: AircraftState }
  | { type: 'CHECK_ALL' }
  | { type: 'UNCHECK_ALL' }
  | { type: 'CHECK_ONLY'; payload: { id: string } };
const AircraftStateContext = createContext<
  [AircraftState, React.Dispatch<AircraftAction>]
>([[], () => {}]);
const AIRCRAFT_TAG = 'aircraft';

const reducer = (state: AircraftState, action: AircraftAction) => {
  switch (action.type) {
    case 'SET':
      return action.payload;
    case 'TOGGLE':
      return state.map((aircraft) => {
        if (aircraft.id === action.payload.id)
          return { ...aircraft, checked: !aircraft.checked };
        return aircraft;
      });
    case 'CHECK_ALL':
      return state.map((aircraft) => ({ ...aircraft, checked: true }));
    case 'UNCHECK_ALL':
      return state.map((aircraft) => ({ ...aircraft, checked: false }));
    case 'CHECK_ONLY':
      return state.map((aircraft) => ({
        ...aircraft,
        checked: aircraft.id === action.payload.id ? true : false,
      }));
    default:
      return state;
  }
};
export const AircraftFilterProvider = ({ children }) => {
  const searchRequest = usePerformedSearchRequest();
  const reducerResult = useReducer(reducer, []);
  const [state, dispatch] = reducerResult;
  const initialState = useAvailableAircraftModels();
  const { add, remove } = useOrderFilterTagsActions();
  const orderedTags = useOrderFilterTags();
  const isApplied = !state.every((aircraft) => aircraft.checked);

  useEffect(() => {
    const isTagOn = !!orderedTags.find(({ alias }) => alias === AIRCRAFT_TAG);
    if (isApplied && !isTagOn)
      add({ alias: AIRCRAFT_TAG, undo: () => dispatch({ type: 'CHECK_ALL' }) });
    else if (!isApplied && isTagOn) remove({ alias: AIRCRAFT_TAG });
  }, [isApplied, add, remove, dispatch, orderedTags]);

  // Due to the mobile situation we need to reset the state when the search
  // changes
  useEffect(() => {
    dispatch({ type: 'SET', payload: [] });
  }, [searchRequest, dispatch]);

  useEffect(() => {
    // Checking whether the state is empty or not is important on mobile
    // because the filters are on a different page, so initialState will change
    // and the state get overwritten
    if (!state.length && initialState && initialState.length > 0) {
      dispatch({ type: 'SET', payload: initialState });
    }
  }, [initialState, dispatch, state]);

  return (
    <AircraftStateContext.Provider value={reducerResult}>
      <AircraftPredicateProvider>{children}</AircraftPredicateProvider>
    </AircraftStateContext.Provider>
  );
};
const useAircraftStateContext = () => useContext(AircraftStateContext);
const useAircraftDispatch = () => useContext(AircraftStateContext)[1];
const useAircraftFilterState = () => useContext(AircraftStateContext)[0];

const useAircraftActions = () => {
  const dispatch = useAircraftDispatch();

  return {
    setState: (state: AircraftState) =>
      dispatch({ type: 'SET', payload: state }),
    toggle: (id: string) => dispatch({ type: 'TOGGLE', payload: { id } }),
    checkAll: () => dispatch({ type: 'CHECK_ALL' }),
    uncheckAll: () => dispatch({ type: 'UNCHECK_ALL' }),
    checkOnly: (id: string) =>
      dispatch({ type: 'CHECK_ONLY', payload: { id } }),
  };
};

const useIsAircraftFilterApplied = () => {
  const state = useAircraftFilterState();

  return !state.every((aircraft) => aircraft.checked);
};

export {
  useAircraftFilterState,
  useAircraftDispatch,
  useAircraftStateContext,
  useAircraftActions,
  useIsAircraftFilterApplied,
};
